import { DataTable } from 'components/DataTable';
import { Room } from 'components/Room';
import Query from 'query/Query';
import React from 'react';

import 'App.css';
import 'components/DataTable.css';
import Schema from 'query/Schema';

import { Spinner } from 'components/Spinner';
import { Loadable, Loaded, LoadError, Loading, LoadingState } from 'LoadingState';
import { Failure, Fetcher } from 'query/Fetch';
import { QueryList } from 'QueryList';
import { Header } from 'components/Header';
import { FetchError } from 'components/FetchError';
import { Action } from 'actions/Action';
import { ProgressNotifier } from 'actions/ProgressNotifier';
import { User } from 'application/User';
import { PROXY, USER_SERVICE } from './Globals';
import { fromURL } from 'odata/OData';
import { Row } from 'table/Row';
import { LoadableQuery, SkeletalQuery } from 'query/SkeletalQuery';
import Table, { PrimaryKeyValues } from 'table/Table';


interface AppProps { }

interface AppState {
  url: string | null;
  userSchemas: Loadable<Schema[]>;
  transientSchemas: Loadable<Schema[]> | undefined;
  query: Loadable<Query>;
  user: Loadable<User>;
}

class App extends React.Component<AppProps, AppState> {

  public user: Loadable<User>;
  public userPromise?: Promise<User>;

  public actions: Action[] = [];

  public version: number = 0; // For React state.

  private fetcher: Fetcher;
  private userService?: Schema;

  constructor(props: Readonly<AppProps>) {
    super(props);

    this.fetcher = new Fetcher();
    this.fetcher.UsingProxy(PROXY);
    this.user = new Loading<User>();

    // transientSchemas is an array because each $metadata document can define multiple schemas.
    this.state = {
      url: null,
      transientSchemas: new Loading<Schema[]>(), // The schema referred to in the URL or chosen by the user.
      userSchemas: new Loading<Schema[]>(),// The user's schemas, of which metadata is stored in the user service.
      query: new Loading<Query>(),
      user: new Loading<User>()
    }
  }

  // React in dev / strict mode will call componentDidMount twice, and we have side effects here.
  private userAsked = false; 
  componentDidMount = () => {
    // TODO: is the user logged out? Then go into anonymous mode and assume a URL in the params.
    // Otherwise, load everything from the user service for that user.
    // But if the URL in the params is new, ask if we should generate new queries for it.

    // TODO Not needed for localhost only install. 
    // Maybe use a feature flag here.
    // this.loadUser();
    this.localhostOnly();

    const params = new URLSearchParams(window.location.search);
    let url = params.get("url");
    if (null !== url) {
      this.loadTransientSchemas(url);
    } else {
      if (!this.userAsked) {
        this.userAsked = true;
        setTimeout(() => {
          let url = this.promptForUrl();
          if (url) { // quiet the compiler.
            this.loadTransientSchemas(url.toString());
          }
        });
      }
    }
  }

  private promptForUrl() {
    let url : URL|null;
    do {
      let urlStr = prompt("What is your OData Endpoint URL:");
      url = this.clarifyUrl(urlStr);
      if (null===url) {
          alert(`Not a valid URL: ${url}`);
      }
    } while (null===url);
    return url;
  }

  private static readonly METADATA_STR="$metadata";

  /* Be tolerant of whatever the user types in. */
  private clarifyUrl(urlIn: string|null) : URL|null {
    let result : URL;

    if (null===urlIn) {
      return null;
    }
    let urlStr = urlIn;

    if (urlStr.endsWith(App.METADATA_STR)) {
      urlStr = urlStr.slice(0, -App.METADATA_STR.length);
    }

    if (!urlStr.endsWith("/")) {
      urlStr = `${urlStr}/`;
    }

    if (!urlStr.toUpperCase().startsWith("HTTP")) {
      urlStr = `HTTPS://${urlStr}`;
    }

    try {
      return new URL(urlStr);
    } catch (e) {
      return null;
    }
  }

  private loadUser = () => {
    this.fetcher.fetchJSONFromPath("/auth/userInfo").then(userInfo => {
      let user = new User();
      user.userName = userInfo.userName;
      user.isLoggedIn = (null !== userInfo.userName);
      this.user = new Loaded<User>(user);
      this.setState({ user: new Loaded(user) });

      this.loadUsersSchemas();
    }, (error) => {
      this.setState({ user: new LoadError(error.toString()) });
    });
  }

  private localhostOnly() {
    // TODO
  }

  private loadUsersSchemas = () => {
    // First we load from the user's schema DB.
    // Then we load the metadata for each schema.
    fromURL(this.fetcher, USER_SERVICE).then(metadata => {
      try {
        this.userService = metadata[0];
        metadata[0].findTable("Datasets") // TODO: rename to Dataset
          .query()
          .addColumnsByName(["Name", "Url", "Namespace"])
          // TODO .whereEquals("Username", this.user.getValue()?.userName)
          .fetch(this.fetcher, 0, 1000)
          .then((rows) => {
            try {
              let schemas = this.loadUsersSchemasFrom(rows);
              this.setState({ userSchemas: new Loaded<Schema[]>(schemas) });
              this.loadUsersQueries(schemas, metadata[0]);
            } catch (error) {
              this.userSchemasError(error);
            }
          }, (error) => {
            this.userSchemasError(error);
          });

      } catch (e) {
        let error = new LoadError(new Failure((e as Error).toString(), "Internal error: Could not parse userservice data."));
        this.setState({ userSchemas: error as Loadable<Schema[]> });
      }
    }, (error) => {
      this.userSchemasError(error);
    });

  }

  private userSchemasError = (error: any) => {
    let userError: Loadable<any>;
    console.error(error);
    if (error instanceof Error) {
      userError = new LoadError(new Failure(String(error), "Error happened"));
    } else if (error instanceof LoadError) {
      userError = error;
    } else {
      userError = new LoadError(new Failure(String(error), "Unknown Error happened"));
    }

    this.setState({ userSchemas: userError });
  }

  private loadUsersSchemasFrom = (rows: Row[]) => {
    let schemas: Schema[] = [];
    for (let each of rows) {
      let name = each.getValue("Name");
      let url = each.getValue("Url");
      let namespace = each.getValue("Namespace");
      let schema = new Schema(url, namespace);
      schema.name = name;
      schemas.push(schema);
    }
    return schemas;
  }

  /* Load all queries. We give each OData source a chance to store it's own queries.
  If it does not have that capability, we fall back to our centralised query store.
  (But for now, no local OData sources store their own queries so we have only implemented
    the centralised store.
  ).
  We query both at the same time. Umm.. and then we concatenate the results?

  Prerequisites: the userService has loaded.
  */
  private loadUsersQueries = (schemas: Schema[], userService: Schema) => {
    for (let source of schemas) {
      // Load queries from source, if available.
      /* 
      let sourceQueries = source.findTableMaybe("Queries")?.query();
      if (sourceQueries) {
        this.loadUsersQueriesFrom(source, source, sourceQueries);
      } */

      // Load queries from user service, if available.
      let userServiceQueries = userService.findTable("Queries").query()
        .whereEquals("Dataset/Url", source.url);
      this.loadUsersQueriesFrom(source, userService, userServiceQueries);
    }
  }

  private loadUsersQueriesFrom(into: Schema, source: Schema, querySource: Query) {
    // Load from originating schema.
    querySource.addColumnsByName(["Id", "Name"]);

    querySource.fetch(this.fetcher, 0, 1000)
      .then(queries => {
        for (let eachQuery of queries) {
          let id: PrimaryKeyValues = eachQuery.primaryKeyValues;
          let name: string = eachQuery.getValue("Name");
          // We don't need to load the query. We only need to know it's name.
          // We can load the full query later if the user wants to see it.
          into.querys.push(new SkeletalQuery(into, source, id, name));
        }
        this.setState({ userSchemas: new Loaded(this.state.userSchemas.getValue()) });
      })
  }

  // TODO: rename to loadSchemaFromURLParameters
  private loadTransientSchemas: (url: string) => Promise<Loadable<Schema[]>> = (url) => {
    if ("http"!==url.trimStart().substring(0,4).toLowerCase()) {
      url = "http://"+url;
    }

    if (!url.trimEnd().endsWith('/')) {
      url = url.trimEnd() + '/';
    }

    this.setState({ url: url });

    return new Promise<Loadable<Schema[]>>((resolve, _) => {
      let loadingUrlSchema: Promise<Schema[]>;

      loadingUrlSchema = fromURL(this.fetcher, url)
      loadingUrlSchema.then(
        schema => {
          let s = new Loaded<Schema[]>(schema);
          this.setState({ transientSchemas: s });
          this.selectFirstQuery(s);
        },
        error => {
          console.error(error);
          this.setState({ 
            transientSchemas: new LoadError(error as Failure),
            query: new LoadError(error as Failure)
          });
        }
      );
    });
  }


  private selectFirstQuery(schemas: Loadable<Schema[]>) {
    switch (schemas.getState()) {
      case LoadingState.Loaded:

        if (schemas.getValue()?.length > 0
          && schemas.getValue()[0]?.tables?.length > 0) {
          let q: Query = schemas.getValue()[0]?.tables[0]?.query();
          this.setState({ query: new Loaded<Query>(q) });
        } else {
          this.setState({ query: new LoadError(new Failure("No query to show.", "")) });
        }
        break;
      case LoadingState.Loading:
        // Should not happen. This method is only called after it is loaded.
        throw new Error();
      case LoadingState.Error:
        this.setState({ query: new LoadError(schemas.getError()) });
        break;
    }
  }

  render = () => {
    // TODO: we want the actions to stay the same if the query is the same.
    let actions: Action[] = [
      // TODO restore this and finish it.Typescript compare strings
      // new SaveQuery(this, this.state.query)
    ];

    let room;
    switch (this.state.query.getState()) {
      case LoadingState.Loaded:
        room = <Room query={this.state.query.getValue()}>
          <DataTable query={this.state.query.getValue()} menu={actions} fetcher={this.fetcher}/>
        </Room>
        break;
      case LoadingState.Loading:
        room = <Spinner />
        break;
      case LoadingState.Error:
        room = <FetchError error={this.state.query.getError()} />
        break;
    }

    return (
      <>
        <Header user={this.state.user}>
          {this.state.url ?? ""}
        </Header>
        <aside className="choose_table">
          <QueryList
            transientSchemas={this.state.transientSchemas}
            usersSchemas={this.state.userSchemas}
            onSelect={this.onSelectQuery} />
        </aside>
        <main>
          {room}
        </main>
      </>
    );
  }

  onSelectQuery = (selected: LoadableQuery) => {
    if (selected.isLoaded()) {
      this.setState({
        query: new Loaded<Query>(selected as Query)
      });

    } else {
      this.loadAndReplaceQuery(selected);
    }
  }

  public saveQuery(q: Query) {
    try {
      if (!this.userService) {
        console.error("User service not loaded. Network error?");
        // TODO: show error to user.
        return;
      }
      console.warn("TODO");
      let queries: Table = this.userService.findTable("Queries");
      // If the query already has a name, save it.
      if (q.id) {
        queries.update(q.id)
          .set("Name", q.name);
        // TODO: columns and stuff.
        queries.commit(this.fetcher);
      } else {
        // Otherwise TODO prompt for a name and insert it.    
        queries.insert()
          .set("Name", q.name);
        // TODO: columns and stuff.
        queries.commit(this.fetcher);
      }
    } catch (e) {
      console.error(e);
      alert(e);
    }
  }

  private loadAndReplaceQuery = (q: LoadableQuery) => {
    let table = (q as SkeletalQuery).metadataSource.findTable("Queries");
    let qid = q.getId();
    if (null === qid) {
      console.debug(q);
      throw new Error("Retrieved query that has no id.");
    }
    return table.query()
      .addColumnsByName(["Id", "Name"]) // TODO more
      .whereEquals("Id", qid.toString())
      .fetch(this.fetcher, 0, 1)
      .then(qResult => {
        try {
          let row = qResult[0];
          let result: Query = new Query(table);
          result.id = row.getValue("Id");
          result.name = row.getValue("Name");
          // TODO columns
          // TODO filter
          (q as SkeletalQuery).schema.replaceSkeletonWith(q as SkeletalQuery, result);
          this.setState({
            query: new Loaded<Query>(result)
          });
        } catch (error) {
          console.error(error);
          this.setState({ query: new LoadError(new Failure(String(error), "Error happened")) });
        }
      }, error => {
        console.error(error);
        this.setState({ query: new LoadError(error) });
      });
  }

}

class SaveQuery extends Action {

  app: App;
  query: Loadable<Query>;

  constructor(app: App, query: Loadable<Query>) {
    super();
    this.app = app;
    this.query = query;
  }

  label = "Save query"

  canDo(): boolean {
    return LoadingState.Loaded === this.query.getState()
  }

  do(p: ProgressNotifier): void {
    if (this.canDo()) {
      this.app.saveQuery(this.query.getValue());
    }
  }
}

export default App;
